import { createFFmpeg, fetchFile, FFmpeg } from '@ffmpeg/ffmpeg';
import Promise from 'bluebird';
import { VideoUploadFunction } from '../types/transport';
import { getPlaylists, getStreamFilePaths } from './playlist';

const origVideoFile = 'video.mp4';

const masterPlaylist = 'master.m3u8';

const getConversionParameters = (fps: number, origHeight = 1080) => {
  const preset = 'ultrafast';
  const getScaleFilterParameters = (height: string) =>
    `scale=w=ceil(${height}*iw/(ih*2))*2:h=ceil(${height}/2)*2`;
  const streamSettings = [
    {
      video: {
        height: '1080',
        bitrate: '5M',
        bufsize: '10M',
      },
      audio: {
        bitrate: '128k',
      },
    },
    {
      video: {
        height: '720',
        bitrate: '3M',
        bufsize: '3M',
      },
      audio: {
        bitrate: '96k',
      },
    },
    {
      video: {
        height: '360',
        bitrate: '1M',
        bufsize: '1M',
      },
      audio: {
        bitrate: '64k',
      },
    },
  ].filter((_, index, orig) => {
    const nextEntry = orig[index + 1];
    if (!nextEntry) return true;
    return Number(nextEntry.video.height) < origHeight;
  });

  const scalePart = streamSettings
    .map(
      ({ video }, index) =>
        `[v${index + 1}]${getScaleFilterParameters(video.height)}[v${
          index + 1
        }out]`,
    )
    .join(';');

  return [
    '-i',
    origVideoFile,
    '-filter_complex',
    `[0:v]split=${streamSettings.length}${streamSettings
      .map((_, index) => `[v${index + 1}]`)
      .join('')}; ${scalePart}`,
    streamSettings.map(({ video }, index) => [
      '-map',
      `[v${index + 1}out]`,
      `-c:v:${index}`,
      'libx264',
      '-x264-params',
      'nal-hrd=cbr:force-cfr=1',
      `-b:v:${index}`,
      video.bitrate,
      `-maxrate:v:${index}`,
      video.bitrate,
      `-minrate:v:${index}`,
      video.bitrate,
      `-bufsize:v:${index}`,
      video.bufsize,
      '-preset',
      preset,
      '-g',
      `${fps * 2}`,
      '-sc_threshold',
      '0',
      '-keyint_min',
      `${fps * 2}`,
    ]),
    streamSettings.map(({ audio }, index) => [
      '-map',
      'a:0',
      `-c:a:${index}`,
      'aac',
      `-b:a:${index}`,
      audio.bitrate,
      '-ac',
      '2',
    ]),
    '-f',
    'hls',
    '-hls_time',
    '6',
    '-hls_playlist_type',
    'vod',
    '-hls_flags',
    'iframes_only+independent_segments',
    '-hls_segment_type',
    'mpegts',
    '-hls_segment_filename',
    'stream_%v_data%02d.ts',
    '-master_pl_name',
    masterPlaylist,
    '-var_stream_map',
    streamSettings.map((_, index) => `v:${index},a:${index}`).join(' '),
    'stream_%v.m3u8',
  ].flat(3);
};

const getVideoMetadata = async (ffmpeg: FFmpeg) => {
  let fps: number | undefined;
  let width: number | undefined;
  let height: number | undefined;
  ffmpeg.setLogger((logParams: { type: string; message: string }) => {
    if (logParams.type === 'fferr') {
      const match = logParams.message.match(
        /Video:.* ([0-9]*x[0-9]*)[, ].*, ([0-9]*) fps,/,
      );
      if (match && match.length === 3) {
        fps = Number(match[2]);
        const dimension = match[1].split('x');
        width = Number(dimension[0]);
        height = Number(dimension[1]);
      }
    }
  });

  try {
    // only use ffmpeg to get data, ignoring the missing output file error
    await ffmpeg.run('-i', origVideoFile, '-t', '1', 'tmp.ts');
  } catch (e) {
    console.log('err here', e);
  }
  if (!fps) {
    throw new Error('could not find framerate');
  }
  ffmpeg.setLogger(() => {});
  return {
    fps,
    width,
    height,
  };
};

export interface ProgressEntry {
  stage: 'transcoding' | 'uploading';
  progress: number;
}

type ProgressCallback = (entry: ProgressEntry) => unknown;

const uploadPlaylist = async (
  filename: string,
  ffmpeg: FFmpeg,
  videoUploadTransport: VideoUploadFunction,
  progress: ProgressCallback,
) => {
  let playlistFileContent = new TextDecoder().decode(
    ffmpeg.FS('readFile', filename),
  );
  const videoFileNames = getStreamFilePaths(playlistFileContent);
  const filesData = await Promise.mapSeries(
    videoFileNames,
    async (videoFileName, index) => {
      const videoContent = ffmpeg.FS('readFile', videoFileName);
      const url = await videoUploadTransport(videoFileName, videoContent);
      ffmpeg.FS('unlink', videoFileName);
      progress({
        stage: 'uploading',
        progress: index / videoFileNames.length,
      });
      return {
        videoFileName,
        url,
      };
    },
  );

  // modify playlist
  filesData.forEach(({ videoFileName, url }) => {
    playlistFileContent = playlistFileContent.replace(videoFileName, url);
    // set higher version (wrong output by our ffmpeg version)
    playlistFileContent = playlistFileContent.replace(
      '#EXT-X-VERSION:3',
      '#EXT-X-VERSION:4',
    );
  });

  return videoUploadTransport(
    filename,
    new TextEncoder().encode(playlistFileContent),
  );
};

export const processVideo = async (
  videoData: string | Buffer | Blob | File,
  videoUploadTransport: VideoUploadFunction,
  progressCallback: ProgressCallback,
): Promise<string> => {
  const ffmpeg = createFFmpeg({
    log: false,
    corePath: '/ffmpeg/ffmpeg-core.js',
  });
  await ffmpeg.load();
  ffmpeg.FS('writeFile', origVideoFile, await fetchFile(videoData));
  const { fps, height } = await getVideoMetadata(ffmpeg);

  ffmpeg.setProgress(data => {
    progressCallback({
      stage: 'transcoding',
      progress: data.ratio,
    });
  });
  await ffmpeg.run(...getConversionParameters(fps, height));

  let masterPlaylistContent = new TextDecoder().decode(
    ffmpeg.FS('readFile', masterPlaylist),
  );
  const playlists = getPlaylists(masterPlaylistContent);

  const playlistLinks = await Promise.mapSeries(playlists, (filename, index) =>
    uploadPlaylist(
      filename,
      ffmpeg,
      videoUploadTransport,
      (entry: ProgressEntry) => {
        progressCallback({
          stage: 'uploading',
          progress:
            index / playlists.length + entry.progress / playlists.length,
        });
      },
    ),
  );

  // replace urls and upload
  playlists.forEach((playlistFile, index) => {
    masterPlaylistContent = masterPlaylistContent.replace(
      playlistFile,
      playlistLinks[index],
    );
  });

  try {
    ffmpeg.exit();
  } catch (e) {
    console.warn('exit failed', e);
  }

  return videoUploadTransport(
    masterPlaylist,
    new TextEncoder().encode(masterPlaylistContent),
  );
};
